import moment from 'moment-timezone';
import DeliverySetting from '../../types/DeliverySetting';
import {
  ExtraTransportAttributes,
  ExtraTransportType,
  ScheduleDatesLike,
  TransportChange,
} from '../../types/RentalVersionData';

// TD1-10271 D-3 (raised to 8 in TD1-10507): cap on extra legs per order — the order's own two
// stops (First Drop Off / Last Pick Up) are not counted, so the order holds at most 10 stops. This
// is the front end's single source for the cap; it must track the backend's
// `RentalTransport::MAX_EXTRA_LEGS`.
export const MAX_EXTRA_TRANSPORTS = 8;

// Delivery types that own routing stops (Rental::REQUIRE_DELIVERY).
export const DELIVERY_TYPES_WITH_TRANSPORTS = ['default_delivery', 'custom_delivery'];

// D-29: labels are "Drop Off n" / "Pick Up n", never "D2" / "P2".
export const TRANSPORT_TYPE_LABELS: Record<ExtraTransportType, string> = {
  drop_off: 'Drop Off',
  pick_up: 'Pick Up',
};

// Row index (the same indexes the API uses) → the messages for that row; -1 holds messages that
// name no row.
export type ExtraTransportRowErrors = Record<number, string[]>;

// The delivery-time presets a row can follow (D-9 amended). "clock" is a specific start and end;
// `no_preference` is not offered — on the main windows it copies the event dates, which has no
// meaning for an extra leg.
export const EXTRA_TRANSPORT_PRESETS = ['morning', 'afternoon', 'evening'] as const;
export type ExtraTransportPreference = 'clock' | (typeof EXTRA_TRANSPORT_PRESETS)[number];

export const toExtraTransportDate = (value: Date | string | null | undefined): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toDate = toExtraTransportDate;

export const isActiveExtraTransport = (row: ExtraTransportAttributes) =>
  row._destroy !== '1';

const byStartDateTime = (a: ExtraTransportAttributes, b: ExtraTransportAttributes) => {
  const at = toDate(a.startDateTime)?.getTime();
  const bt = toDate(b.startDateTime)?.getTime();
  return at === undefined || bt === undefined ? 0 : at - bt;
};

// TD1-10549: ordered once, when the rental is opened — drop-offs and pick-ups each by start date,
// earliest first; a save-in-place or an edit must never re-run this (see the reducer call sites).
// Equal or missing dates fall back to the original position, so this relies on `Array#sort` being
// stable (guaranteed since ES2019) rather than re-deriving a tiebreak by hand.
//
// Sorted per type, one call per type, then written back into that type's own slots — a single sort
// over the whole array with a comparator that ties different types (return 0) is not transitive
// (drop_off ~ pick_up ~ drop_off does not imply drop_off ~ drop_off) and `Array#sort` does not
// guarantee a correct result for a non-transitive comparator, so a drop-off could stay out of date
// order whenever a pick-up sits between two drop-offs in the stored array. `DeliveryTimes` still
// does the drop-offs-first/pick-ups-last grouping itself; this only has to fix order within a type.
export const sortExtraTransportsForLoad = (
  rows: ExtraTransportAttributes[]
): ExtraTransportAttributes[] => {
  const result = [...rows];

  (Object.keys(TRANSPORT_TYPE_LABELS) as ExtraTransportType[]).forEach((type) => {
    const slots = result
      .map((row, index) => index)
      .filter((index) => result[index].transportType === type);
    const sorted = slots.map((index) => result[index]).sort(byStartDateTime);
    slots.forEach((index, position) => {
      result[index] = sorted[position];
    });
  });

  return result;
};

// The rows the user still sees, keeping their position in the stored array (the API reports
// validation errors by that index).
export const activeExtraTransports = (
  rows: ExtraTransportAttributes[] | undefined
): { row: ExtraTransportAttributes; index: number }[] =>
  (rows ?? [])
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => isActiveExtraTransport(row));

// D-10 amended: a saved row shows the label stored by the API ("Drop Off 2"); an unsaved row is
// named by its 1-based position among the visible rows, which is also how a save-time error
// names it (D-34).
export const extraTransportRowLabel = (
  row: ExtraTransportAttributes,
  position: number
): string => (row.id && row.label ? row.label : `Extra Delivery (${position})`);

// TD1-10551 (product owner ruling, amending TD1-10504) — THE label rule, and the only
// implementation of it in the front end.
//
// Per type:
//   * a type with a single stop is just "Drop Off" / "Pick Up" — no suffix, no prefix;
//   * a type with more than one stop numbers them D1..DN / P1..PN, and the word goes to the edge
//     rank — "First Drop Off D1", "Last Pick Up PN";
//   * the ranks are NOT purely chronological: the order's own stop (`mirror`) is pinned to the edge
//     of its type — the own drop off is always D1 and the own pick up always PN, whatever the dates
//     say. The extras take the remaining ranks in chronological order (start date, then original
//     index to break a tie).
//
// The API computes the identical rule in `Rental#computed_transport_labels` (PR #6919): the mirror
// pinned first / last, the word logic unchanged. Read-only surfaces show the stored label and must
// never recompute it. This exists because the Rental Edit form has to retitle every stop of a type
// the moment a row is added or removed, before any save.
export interface StopLike {
  transportType?: ExtraTransportType | string | null;
  startDateTime?: Date | string | null;
  // The order's own stop of this type — the row carrying the order's own window (set by
  // `formStopTitles`). Pinned to the edge rank of its type.
  mirror?: boolean;
}

const POSITION_WORD: Record<ExtraTransportType, string> = {
  drop_off: 'First',
  pick_up: 'Last',
};

const SUFFIX_LETTER: Record<ExtraTransportType, string> = {
  drop_off: 'D',
  pick_up: 'P',
};

const stopType = (stop: StopLike): ExtraTransportType =>
  stop.transportType === 'pick_up' ? 'pick_up' : 'drop_off';

// Titles for `stops`, returned in the same order as the input so callers can index straight into it.
export const stopTitles = (stops: StopLike[]): string[] => {
  const titles: string[] = new Array(stops.length);

  (Object.keys(TRANSPORT_TYPE_LABELS) as ExtraTransportType[]).forEach((type) => {
    const ofType = stops
      .map((stop, index) => ({ stop, index }))
      .filter(({ stop }) => stopType(stop) === type)
      .sort((a, b) => {
        // The order's own stop takes the edge rank of its type whatever its date: first among the
        // drop offs, last among the pick ups.
        const am = a.stop.mirror ? 0 : 1;
        const bm = b.stop.mirror ? 0 : 1;
        const pinned = type === 'pick_up' ? bm - am : am - bm;
        if (pinned !== 0) return pinned;

        const at = a.stop.startDateTime ? new Date(a.stop.startDateTime).getTime() : 0;
        const bt = b.stop.startDateTime ? new Date(b.stop.startDateTime).getTime() : 0;
        // Original position breaks a tie so the order is stable while a new row shares a date.
        return at - bt || a.index - b.index;
      });

    const base = TRANSPORT_TYPE_LABELS[type];

    ofType.forEach(({ index }, position) => {
      if (ofType.length < 2) {
        titles[index] = base;
        return;
      }

      const numbered = `${base} ${SUFFIX_LETTER[type]}${position + 1}`;
      const isEdge = type === 'drop_off' ? position === 0 : position === ofType.length - 1;
      titles[index] = isEdge ? `${POSITION_WORD[type]} ${numbered}` : numbered;
    });
  });

  return titles;
};

interface MirrorStops {
  dropOffStart: Date | null;
  pickUpStart: Date | null;
}

// The would-be dates of Drop Off 1 / Pick Up 1 from the form's schedule fields, mirroring the
// backend normalisers: with "Add Delivery Times" off (or a no_preference setting) the window
// collapses onto the order start / end.
export const mirrorStopsFromSchedule = (scheduleAttributes: ScheduleDatesLike = {}): MirrorStops => {
  const {
    sameAsEventDate,
    eventStartSetting,
    eventEndSetting,
    eventStartDateTime,
    eventEndDateTime,
    startDateTime,
    endWindowBeginning,
  } = scheduleAttributes ?? {};

  const dropOffStart =
    sameAsEventDate || eventStartSetting === 'no_preference'
      ? eventStartDateTime
      : startDateTime ?? eventStartDateTime;
  const pickUpStart =
    sameAsEventDate || eventEndSetting === 'no_preference'
      ? eventEndDateTime
      : endWindowBeginning ?? eventEndDateTime;

  return { dropOffStart: toDate(dropOffStart), pickUpStart: toDate(pickUpStart) };
};

// A sensible starting point for a new row, anchored on the order's own stop of the same type and
// stepping into the order: a drop-off starts the day after Drop Off 1 and never runs past the order
// end; a pick-up starts the day before Pick Up 1 and never runs before the order start. A same-day
// order gets one hour instead of one day, still clamped (a zero-length window yields the bound
// itself rather than a time outside it).
export const defaultExtraTransportStart = (
  scheduleAttributes: ScheduleDatesLike,
  transportType: ExtraTransportType = 'drop_off'
): Date => {
  const { dropOffStart, pickUpStart } = mirrorStopsFromSchedule(scheduleAttributes);

  if (transportType === 'pick_up') {
    const start = pickUpStart ?? new Date();
    const lower = dropOffStart ?? toDate(scheduleAttributes?.eventStartDateTime);

    let candidate = moment(start).subtract(1, 'day');
    if (lower && candidate.isBefore(lower)) candidate = moment(start).subtract(1, 'hour');
    if (lower && candidate.isBefore(lower)) candidate = moment(lower);

    return candidate.toDate();
  }

  const start = dropOffStart ?? new Date();
  const end = pickUpStart ?? toDate(scheduleAttributes?.eventEndDateTime);

  let candidate = moment(start).add(1, 'day');
  if (end && candidate.isAfter(end)) candidate = moment(start).add(1, 'hour');
  if (end && candidate.isAfter(end)) candidate = moment(end);

  return candidate.toDate();
};

const sameClockTime = (first: Date, second: Date) =>
  first.getHours() === second.getHours() && first.getMinutes() === second.getMinutes();

// The preference is not persisted (D-9 amended): a row whose start and end fall on one day and
// match a configured preset's start / end times (hour and minute, as `combineDateAndTime` sets
// them) is shown under that preset; anything else is a specific time ("clock").
export const deriveExtraTransportPreference = (
  row: ExtraTransportAttributes,
  deliverySetting: DeliverySetting = {}
): ExtraTransportPreference => {
  const start = toDate(row.startDateTime);
  const end = toDate(row.endDateTime);
  if (!start || !end || !moment(start).isSame(end, 'day')) return 'clock';

  const preset = EXTRA_TRANSPORT_PRESETS.find((name) => {
    const presetStart = toDate(deliverySetting[`${name}StartTime`]);
    const presetEnd = toDate(deliverySetting[`${name}EndTime`]);
    return !!presetStart && !!presetEnd && sameClockTime(start, presetStart) && sameClockTime(end, presetEnd);
  });

  return preset ?? 'clock';
};

// The order's effective start / end as the API will see them after its Schedule normalisers: with
// "Add Delivery Times" off (`sameAsEventDate`) or a `no_preference` setting the window fields are
// overwritten with the event dates (`make_same_as_event_dates`,
// `update_times_based_on_delivery_preference`), so the form's `startDateTime` / `endDateTime` may
// be stale and the event dates win.
const effectiveOrderDates = (
  scheduleAttributes: ScheduleDatesLike = {}
): { start: Date | null; end: Date | null } => {
  const {
    sameAsEventDate,
    eventStartSetting,
    eventEndSetting,
    eventStartDateTime,
    eventEndDateTime,
    startDateTime,
    endDateTime,
  } = scheduleAttributes ?? {};

  const start =
    sameAsEventDate || eventStartSetting === 'no_preference'
      ? eventStartDateTime
      : startDateTime ?? eventStartDateTime;
  const end =
    sameAsEventDate || eventEndSetting === 'no_preference'
      ? eventEndDateTime
      : endDateTime ?? eventEndDateTime;

  return { start: toDate(start), end: toDate(end) };
};

// D-11: the order window every leg must fall in, mirroring `Schedule#set_off_shelf_time`:
// * "Add Off-Shelf Time" OFF (`shelfEnabled` false) → the order's effective start / end; the
//   form's `offShelfAt` / `onShelfAt` are ignored — they keep whatever was last saved or copied
//   and the API overwrites them with start / end on save;
// * "Add Off-Shelf Time" ON → `offShelfAt` / `onShelfAt`, each clamped to the outer of it and the
//   order date (the shelf window always contains the order window; a missing shelf value falls
//   back to the order date).
export const extraTransportOrderWindow = (
  scheduleAttributes: ScheduleDatesLike = {}
): { lower: Date | null; upper: Date | null } => {
  const { start, end } = effectiveOrderDates(scheduleAttributes);
  if (!scheduleAttributes?.shelfEnabled) return { lower: start, upper: end };

  const offShelf = toDate(scheduleAttributes.offShelfAt);
  const onShelf = toDate(scheduleAttributes.onShelfAt);
  const lower = offShelf && start ? new Date(Math.min(offShelf.getTime(), start.getTime())) : offShelf ?? start;
  const upper = onShelf && end ? new Date(Math.max(onShelf.getTime(), end.getTime())) : onShelf ?? end;

  return { lower, upper };
};

// "(Aug 27, 2026 10:55 PM – Aug 28, 2026 10:55 PM)" — the same bounds the API prints (D-34).
const formatOrderWindowBounds = (lower: Date, upper: Date, timeZone?: string | null) => {
  const format = (date: Date) =>
    (timeZone ? moment(date).tz(timeZone) : moment(date)).format('MMM D, YYYY h:mm A');
  return `(${format(lower)} – ${format(upper)})`;
};

const secondsOf = (date: Date) => Math.floor(date.getTime() / 1000);

// TD1-10551: the "outside the order window" messages alone, for each active row — the same strings
// `validateExtraTransports` produces at save time and the same wording the API sends back (D-11 /
// D-34), so the flag the form raises while the user is typing and the error a save returns read
// identically. The order's own two stops are never checked: they ARE the window.
export const extraTransportWindowErrors = (
  rows: ExtraTransportAttributes[] | undefined,
  scheduleAttributes: ScheduleDatesLike = {}
): ExtraTransportRowErrors => {
  const errors: ExtraTransportRowErrors = {};
  const { lower, upper } = extraTransportOrderWindow(scheduleAttributes);
  if (!lower || !upper) return errors;

  const bounds = formatOrderWindowBounds(lower, upper, scheduleAttributes?.timeZone);
  const outside = (date: Date) => date < lower || date > upper;

  activeExtraTransports(rows).forEach(({ row, index }) => {
    const start = toDate(row.startDateTime);
    if (!start) return;
    // The API keeps end = start when no end is sent.
    const end = toDate(row.endDateTime) ?? start;

    const messages: string[] = [];
    if (outside(start)) messages.push(`Date must be within the order dates ${bounds}`);
    if (end.getTime() !== start.getTime() && outside(end)) {
      messages.push(`End date must be within the order dates ${bounds}`);
    }

    if (messages.length > 0) errors[index] = messages;
  });

  return errors;
};

// D-11 / F-9 client side, mirroring RentalTransport's extra-leg validations message for message:
// date present, type valid, start ≤ end, both inside the order window, no two same-type stops
// (Drop Off 1 / Pick Up 1 included) starting at the same time. Returns messages by array index.
export const validateExtraTransports = (
  rows: ExtraTransportAttributes[] | undefined,
  scheduleAttributes: ScheduleDatesLike = {}
): ExtraTransportRowErrors => {
  const errors: ExtraTransportRowErrors = {};
  // The window rules are the ones the form already shows live (TD1-10551) — one implementation.
  const windowErrors = extraTransportWindowErrors(rows, scheduleAttributes);

  const mirrors = mirrorStopsFromSchedule(scheduleAttributes);
  const startsSeen: Record<ExtraTransportType, Set<number>> = { drop_off: new Set(), pick_up: new Set() };
  if (mirrors.dropOffStart) startsSeen.drop_off.add(secondsOf(mirrors.dropOffStart));
  if (mirrors.pickUpStart) startsSeen.pick_up.add(secondsOf(mirrors.pickUpStart));

  activeExtraTransports(rows).forEach(({ row, index }) => {
    const messages: string[] = [];
    const start = toDate(row.startDateTime);
    // The API keeps end = start when no end is sent.
    const end = toDate(row.endDateTime) ?? start;
    const type = row.transportType as ExtraTransportType | null | undefined;
    const typeValid = !!type && type in TRANSPORT_TYPE_LABELS;

    if (!start) messages.push('Date is required');
    if (!typeValid) messages.push('Type must be Drop Off or Pick Up');
    if (start && end && end < start) messages.push('End date must not be before the start date');
    messages.push(...(windowErrors[index] ?? []));
    if (start && typeValid && type) {
      const key = secondsOf(start);
      if (startsSeen[type].has(key)) {
        messages.push(`Another ${TRANSPORT_TYPE_LABELS[type]} of this order already starts at this date and time`);
      } else {
        startsSeen[type].add(key);
      }
    }

    if (messages.length > 0) errors[index] = messages;
  });

  return errors;
};

// TD1-10504: the titles the Rental Edit form is showing right now, for the whole stop set — the
// order's own two stops plus the extra legs. Built from `stopTitles`, so the row headings, the
// mirror-column headings and the error prefixes are all the same strings by construction.
//
// `byRowIndex` is keyed by position in the stored `extraTransportsAttributes` array, which is also
// how the API indexes its row errors (D-34), so an error can be named without a second lookup.
export interface FormStopTitles {
  mirrorDropOff: string;
  mirrorPickUp: string;
  byRowIndex: Record<number, string>;
}

export const formStopTitles = (
  scheduleAttributes: ScheduleDatesLike = {},
  rows: ExtraTransportAttributes[] | undefined = []
): FormStopTitles => {
  const { dropOffStart, pickUpStart } = mirrorStopsFromSchedule(scheduleAttributes);
  const active = activeExtraTransports(rows);

  const stops: StopLike[] = [
    { transportType: 'drop_off', startDateTime: dropOffStart, mirror: true },
    { transportType: 'pick_up', startDateTime: pickUpStart, mirror: true },
    ...active.map(({ row }) => ({
      transportType: row.transportType,
      startDateTime: toDate(row.startDateTime),
    })),
  ];

  const titles = stopTitles(stops);
  const byRowIndex: Record<number, string> = {};
  active.forEach(({ index }, position) => {
    byRowIndex[index] = titles[position + 2];
  });

  return { mirrorDropOff: titles[0], mirrorPickUp: titles[1], byRowIndex };
};

// The on-screen title of the extra row at `index`. Falls back to the wire wording (D-34) when the
// row is not in hand, so a message is never left unnamed.
const rowTitleAt = (
  rows: ExtraTransportAttributes[] | undefined,
  index: number,
  scheduleAttributes?: ScheduleDatesLike
): string => {
  if (!(rows ?? [])[index]) return `Extra Delivery (${index + 1})`;

  return formStopTitles(scheduleAttributes, rows).byRowIndex[index] ?? `Extra Delivery (${index + 1})`;
};

// TD1-10480 QA round 2: a row error names the row exactly as the row's own heading does — if the
// user is looking at "Pick Up 3", the message reads "Pick Up 3: Date must be within the order
// dates". Both the heading and this prefix come from `stopTitles`, so they cannot drift.
//
// The wire contract is unchanged: the API still sends and we still parse
// "Extra Delivery (n): <message>" (n 1-based, D-34). Only what the user reads is swapped, and it is
// swapped here because the FE is the only place that knows the title on screen — a
// backend-computed label would diverge exactly when a row's type has just been flipped and not yet
// saved, which is the case a user is most likely to hit.
export const formatExtraTransportErrors = (
  rowErrors: ExtraTransportRowErrors,
  rows?: ExtraTransportAttributes[],
  scheduleAttributes?: ScheduleDatesLike
): string[] =>
  Object.entries(rowErrors).flatMap(([index, messages]) =>
    messages.map((message) =>
      Number(index) < 0
        ? message
        : `${rowTitleAt(rows, Number(index), scheduleAttributes)}: ${message}`
    )
  );

// The API's "Extra Delivery (n): <message>" strings, re-prefixed with each row's on-screen title for
// the summary toast. A message naming no row is passed through untouched.
export const relabelExtraTransportApiErrors = (
  messages: unknown,
  rows?: ExtraTransportAttributes[],
  scheduleAttributes?: ScheduleDatesLike
): string[] => {
  if (!Array.isArray(messages)) return [];

  return messages.map((message) => {
    const match = /^Extra Delivery \((\d+)\): ([\s\S]*)$/.exec(String(message));
    if (!match) return String(message);

    return `${rowTitleAt(rows, Number(match[1]) - 1, scheduleAttributes)}: ${match[2]}`;
  });
};

// Maps the API's `extra_transports` errors ("Extra Delivery (n): <message>", n 1-based, D-34) to
// row indexes. Messages that name no row land under -1 so the caller can show them globally.
export const parseExtraTransportApiErrors = (
  messages: unknown
): ExtraTransportRowErrors => {
  const errors: ExtraTransportRowErrors = {};
  if (!Array.isArray(messages)) return errors;

  messages.forEach((message) => {
    const match = /^Extra Delivery \((\d+)\): ([\s\S]*)$/.exec(String(message));
    const index = match ? Number(match[1]) - 1 : -1;
    const text = match ? match[2] : String(message);
    if (!errors[index]) errors[index] = [];
    errors[index].push(text);
  });

  return errors;
};

// The stops the save-time "Update routing?" modal must list (D-27 amended): a persisted stop with
// dependents whose start would MOVE. A stop that would be removed is left out, because removing a
// row is confirmed on the spot by RemoveExtraDeliveriesModal — so a removal is confirmed exactly
// once. Switching the delivery type away from delivery has no such prompt and destroys every stop
// of the order, so that path passes `deliveryRemoved` and the removals are confirmed here instead
// (TD1-10541 P1-9). Empty for a new order (the preview itself is empty then).
//
// TD1-10480 QA round 2 (§6): switching an extra leg's type re-routes the stop exactly as moving its
// dates does, and `TransportChangesPreviewService` now reports that itself — `changed` covers a flip
// and each entry carries `newTransportType`. So this reads the preview rather than re-deriving the
// flip from the rows: one source, and the preview and the modal cannot disagree.
export const transportChangesNeedingConfirmation = (
  changes: TransportChange[] | undefined,
  deliveryRemoved = false
): TransportChange[] =>
  (changes ?? []).filter(
    (change) => change.hasDependents && change.changed && (!change.removed || deliveryRemoved)
  );

// D-27 amended, for unchecking "Extra Deliveries" / removing a row: the persisted rows among
// `rows` that still have dependents in the latest preview. Unsaved rows never need confirmation.
export const extraTransportsWithDependents = (
  rows: ExtraTransportAttributes[],
  changes: TransportChange[] | undefined
): TransportChange[] => {
  const ids = new Set(rows.map((row) => row.id).filter((id): id is number => typeof id === 'number'));
  return (changes ?? []).filter((change) => ids.has(change.id) && change.hasDependents);
};

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

// "Drop Off 2 — Aug 27: 1 truck, load list with 12 items"
export const describeTransportChange = (
  change: TransportChange,
  timeZone?: string
): string => {
  const when = change.currentStartDateTime
    ? (timeZone
        ? moment(change.currentStartDateTime).tz(timeZone)
        : moment(change.currentStartDateTime)
      ).format('MMM D')
    : null;
  const parts: string[] = [];
  if (change.trucksCount > 0) parts.push(plural(change.trucksCount, 'truck'));
  if (change.loadListItemsCount > 0)
    parts.push(`load list with ${plural(change.loadListItemsCount, 'item')}`);
  if (parts.length === 0) parts.push('driver assignment');

  // A flip is worth naming: "Drop Off 3 → Pick Up — Sep 23" reads as the reason the routing goes.
  const retyped =
    !!change.newTransportType && change.newTransportType !== change.transportType
      ? ` → ${TRANSPORT_TYPE_LABELS[change.newTransportType] ?? change.newTransportType}`
      : '';
  const head = [`${change.label ?? ''}${retyped}`.trim(), when].filter(Boolean).join(' — ');
  const suffix = change.removed ? ' (stop removed)' : '';

  return `${head}${suffix}: ${parts.join(', ')}`;
};
